/** Colours for the particle orb: mixing in linear light, and turning any CSS colour into RGB. */

export type Rgb = [number, number, number];

export const ERROR_FROM: Rgb = [0xfb, 0x71, 0x85];
export const ERROR_TO: Rgb = [0xf4, 0x3f, 0x5e];

const toLinear = (c: number): number => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};

const toSrgb = (v: number): number => {
  const c = v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055;
  return Math.min(255, Math.max(0, c * 255));
};

export const mixRgb = (a: Rgb, b: Rgb, t: number): Rgb => [
  toSrgb(toLinear(a[0]) + (toLinear(b[0]) - toLinear(a[0])) * t),
  toSrgb(toLinear(a[1]) + (toLinear(b[1]) - toLinear(a[1])) * t),
  toSrgb(toLinear(a[2]) + (toLinear(b[2]) - toLinear(a[2])) * t),
];

export const rgba = ([r, g, b]: Rgb, alpha: number): string =>
  `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${Math.min(1, Math.max(0, alpha)).toFixed(3)})`;

/**
 * The RGB of a colour as the page computes it (a theme's accent may be a color-mix()): the browser
 * resolves it on the element, a 1-pixel canvas turns the result into numbers. `fallback` without a canvas.
 */
export function resolveColor(el: Element, cssColor: string, fallback: Rgb): Rgb {
  const probe = document.createElement('span');
  probe.style.color = cssColor;
  probe.style.display = 'none';
  el.appendChild(probe);
  const computed = getComputedStyle(probe).color;
  probe.remove();
  const ctx = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
  if (!ctx || !computed) return fallback;
  ctx.fillStyle = computed;
  ctx.fillRect(0, 0, 1, 1);
  const [r = 0, g = 0, b = 0] = ctx.getImageData(0, 0, 1, 1).data;
  return [r, g, b];
}
